import React, { useState } from 'react';
import { Pressable, View, StyleProp, ViewStyle } from 'react-native';
import { Bevel } from './Bevel';
import { Text } from './Text';
import { makeStyles, useTheme } from './ThemeContext';

export interface ButtonProps {
  title?: string;
  onPress?: () => void;
  disabled?: boolean;
  /** Draws the extra outline the platform gave the default action. */
  primary?: boolean;
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
  accessibilityLabel?: string;
}

export function Button({
  title,
  onPress,
  disabled,
  primary,
  style,
  children,
  accessibilityLabel,
}: ButtonProps) {
  const styles = useStyles();
  const theme = useTheme();
  const [held, setHeld] = useState(false);
  const active = held && !disabled;

  const body = (
    <Bevel
      variant={active ? 'pressed' : 'raised'}
      style={styles.bevel}
      contentStyle={styles.content}
    >
      {/* A pressed button nudges its label down-right by one pixel. */}
      <View style={active ? styles.shiftPressed : undefined}>
        {children ?? (
          <Text disabled={disabled} style={styles.label} numberOfLines={1}>
            {title}
          </Text>
        )}
      </View>
    </Bevel>
  );

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      onPressIn={() => setHeld(true)}
      onPressOut={() => setHeld(false)}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ disabled: !!disabled }}
      accessibilityLabel={accessibilityLabel ?? title}
      style={[styles.root, style]}
    >
      {primary ? (
        <View
          style={[
            styles.defaultOutline,
            { borderRadius: theme.metrics.radius ? theme.metrics.radius + 2 : 0 },
          ]}
        >
          {body}
        </View>
      ) : (
        body
      )}
    </Pressable>
  );
}

const useStyles = makeStyles((t) => ({
  root: { minHeight: t.metrics.touchTarget, justifyContent: 'center' as const },
  defaultOutline: { borderWidth: 1, borderColor: t.colors.darkShadow, padding: 1 },
  bevel: { minWidth: 75 },
  content: {
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  label: { fontSize: t.fontSizes.ui, textAlign: 'center' as const },
  shiftPressed: { transform: [{ translateX: 1 }, { translateY: 1 }] },
}));
